<script setup>
// Imports
// Components imports
import DialogDeletePaginated from 'components/dialogs/DialogDeletePaginated.vue'
import ContactForm from 'pages/customer/ContactForm.vue'
import { format } from 'quasar'
import { useServiceList } from 'src/composables/useServiceList'
import { contactSchema } from 'src/schemas/contactSchema.js'
import { countries } from 'src/schemas/countrySchema.js'
import { createFormInstance, generateFormErrorsModel, onError, openDialogForm, schemaToForm } from 'src/utils/utils.js'

// Constants
const api = useFeathers()
const $q = useQuasar()
const router = useRouter()
const { capitalize } = format
const { t } = useI18n({ useScope: 'global' })

const itemsService = 'contacts'
const service = api.service(itemsService)

// Component props
const componentProps = defineProps({
  included: {
    type: Boolean,
    required: false,
    default: false,
  },
  needsToUpdate: {
    type: Boolean,
    required: false,
    default: false,
  },
})
const emit = defineEmits(['update:contacts'])

// Stores
const authStore = useAuthStore()

// Refs
const schema = ref(
  createFormInstance(
    [
      'customer',
      'companyName',
      'firstName',
      'lastName',
      'email',
      'street',
      'houseNumber',
      'houseNumberSuffix',
      'postalCode',
      'city',
      'state',
      'country',
      'jobTitle',
      'tel',
      'registrar',
      'fax',
      'dateBirth',
      'placeBirth',
      'countryBirth',
      'postalBirth',
      'idNumber',
      'regNumber',
      'vatNumber',
      'tmNumber',
      'tmCountry',
      'tmName',
      'idCardDate',
      'idCardIssuer',
      'xxxMemberId',
      'xxxPassword',
      'ensId',
      'ensPassword',
      'profession',
      'travelUinId',
      'hkIndustryType',
    ],
    contactSchema,
  ),
)
const validationErrors = ref({})
const { selectedCustomerInfo } = storeToRefs(authStore)
const originalContactToEdit = ref(null)
const contactToEdit = ref(undefined)
const tableLoading = ref(false)
const contactLoading = ref(false)

// query params
const skipRef = ref(0)
const limitRef = ref(10)
const queryRef = ref({})
const search = ref('')

// Computed properties
const params = computed(() => {
  const query = {
    ...queryRef.value,
  }

  if (search.value !== '') {
    query['textSearch'] = search.value
  }

  query['$sort'] = {
    firstName: 1,
    lastName: 1,
  }

  return { query }
})

const itemsCount = computed(() => {
  return items$.total
})

const items = computed(() => {
  return items$.data
})

const translationString = computed(() => {
  let prefix = process.env.FRONTEND_TYPE + '.' + capitalize(itemsService) + '.'
  if (itemsService === 'users') {
    prefix = 'Shared.Users.'
  }
  return prefix
})

// Methods
const refreshItemList = async () => {
  await items$.find()
}

const newContact = () => {
  contactToEdit.value = service.new(schemaToForm(schema.value, {}))
}

const saveDomainContact = async (data, remove = false) => {
  contactLoading.value = true

  if (!remove) {
    contactToEdit.value.customer = selectedCustomerInfo.value._id
  }

  const existingContact = !!contactToEdit.value?._id

  if (remove) {
    let options = {
      type: 'delete',
      component: DialogDeletePaginated,
      itemsService,
      props: data,
      customProps: {
        preventItemClear: true,
      },
      onOk: async () => {
        await refreshItemList()
        emit('update:contacts')
      },
    }
    options.itemName = data.firstName && data.lastName ? data.firstName + ' ' + data.lastName : ''
    openDialogForm(options)
    return
  }

  const response = await contactToEdit.value.save().catch(async (err) => {
    if (err.errors?.errorCode === 'OXXA_API_RESULT_DIFF') {
      originalContactToEdit.value = await service.get(contactToEdit.value._id)
      contactToEdit.value = originalContactToEdit.value.clone()
    }

    onError(
      {
        type: existingContact ? 'edit' : 'new',
        message: err.message,
        errors: err.errors,
      },
      'contacts',
    )
    validationErrors.value = generateFormErrorsModel(err, itemsService)
    return false
  })

  if (response) {
    $q.notify({
      type: 'positive',
      message: t('Customer.' + capitalize(itemsService) + '.' + (existingContact ? 'Edit' : 'New') + ' item success'),
      progress: true,
    })
    contactToEdit.value = undefined
    originalContactToEdit.value = null
    emit('update:contacts')
  }

  contactLoading.value = false
}

const cancelContactForm = () => {
  validationErrors.value = {}
  contactToEdit.value = undefined
  originalContactToEdit.value = null
}

const setContactToEdit = async (contact) => {
  originalContactToEdit.value = await service.get(contact._id).catch((err) => {
    onError(
      {
        type: 'new',
        message: err.message,
        errors: err.errors,
      },
      'contacts',
    )
    return null
  })

  contactToEdit.value = originalContactToEdit.value.clone()
}

// List data
const items$ = useServiceList(service, params, {
  pagination: {
    limit: limitRef,
    skip: skipRef,
  },
})

// Watchers
watch(
  () => contactToEdit.value,
  async () => {
    if (typeof contactToEdit.value !== 'undefined') {
      if (contactToEdit.value._id) {
        if (schema.value.customer) {
          schema.value.customer.readonly = true
        }
        schema.value.companyName.readonly = true
        schema.value.firstName.readonly = true
        schema.value.lastName.readonly = true
        schema.value.registrar.readonly = true
      } else {
        if (schema.value.customer) {
          schema.value.customer.readonly = false
        }
        schema.value.companyName.readonly = false
        schema.value.firstName.readonly = false
        schema.value.lastName.readonly = false
        schema.value.registrar.readonly = false
      }
    }
  },
  {
    deep: true,
  },
)
watch(
  () => selectedCustomerInfo.value?._id,
  async (newCustomerId, oldCustomerId) => {
    if (!newCustomerId || !oldCustomerId || newCustomerId === oldCustomerId) {
      return
    }
    if (authStore.hasServicePermission(itemsService, ['find_assigned']) && authStore.hasServicePermission(itemsService, ['get_assigned'])) {
      await refreshItemList()
    } else {
      await router.push({ name: 'main' })
    }
  },
)

watch(
  () => componentProps.needsToUpdate,
  async (newValue) => {
    if (newValue) {
      await refreshItemList()
    }
  },
)

// Lifecycle hooks
onMounted(async () => {
  if (!authStore.hasServicePermission(itemsService, ['find_assigned']) || !authStore.hasServicePermission(itemsService, ['get_assigned'])) {
    await router.push({ name: 'main' })
    return
  }
  schema.value.country.options = countries
})

// Expose
defineExpose({
  tableLoading,
  itemsCount,
  contactToEdit,
  items,
})
</script>

<template>
  <q-linear-progress v-if="tableLoading && !componentProps.included" query class="absolute-top z-top" />
  <div class="flex column q-pa-none" v-show="!tableLoading">
    <q-banner v-if="items$.error" inline-actions class="text-white bg-red">
      <template #avatar>
        <q-avatar icon="warning" />
      </template>
      {{ $t('Shared.Globals.Errors.Error loading data') }}
      <template #action>
        <q-btn icon-right="sync" flat color="" :label="$t('Shared.Globals.Retry')" @click="refreshItemList" />
      </template>
    </q-banner>

    <template v-if="(items$.data?.length > 0 || !!items$.latestQuery?.query?.textSearch) && typeof contactToEdit === 'undefined'">
      <div class="row">
        <div class="col-auto flex">
          <q-btn
            v-if="authStore.hasServicePermission(itemsService, ['create_assigned'])"
            :class="$q.screen.xs ? 'q-mb-sm' : ''"
            icon="add"
            class="q-mb-md"
            color="primary"
            :label="$q.screen.gt.sm ? $t(translationString + 'New item') : ''"
            :aria-label="$t(translationString + 'New item')"
            @click.prevent="newContact"
          >
            <q-tooltip v-if="!$q.screen.gt.sm" :delay="500">
              {{ $t(translationString + 'New item', 2) }}
            </q-tooltip>
          </q-btn>
        </div>
        <div class="col flex justify-end q-pl-sm">
          <q-input
            ref="searchInput"
            v-model="search"
            class="bg-white q-field--error q-mb-md full-width"
            clearable
            color="primary"
            data-lpignore="true"
            debounce="1000"
            dense
            outlined
            :class="{ 'max-width-px-350': $q.screen.gt.xs }"
            :label="$t('Shared.Globals.Search')"
            :lang="useUserStore().regionalSettings"
            @blur="search = $event.target.value"
            @keyup.enter="search = $event.target.value"
          >
            <template #append>
              <q-icon name="search" />
            </template>
          </q-input>
        </div>
      </div>
      <q-card
        v-for="(contact, index) in items$.data"
        :key="contact._id + '-' + index"
        class="rounded-borders q-mb-md"
        :class="{ 'card-shadow': !componentProps.included }"
        flat
        :bordered="!!componentProps.included"
      >
        <q-card-section class="q-pa-sm q-pa-sm-md">
          <div class="row">
            <div class="col-auto flex column text-weight-bolder" v-if="contact.firstName && contact.lastName">
              {{ contact.firstName }} {{ contact.lastName }}
            </div>
            <div class="col-auto flex column text-h6 text-weight-bolder" v-else>-</div>
            <div class="col text-right">{{ contact.companyName }}</div>
          </div>
          <div class="row">
            <div class="col flex column text-caption">{{ contact.email }}</div>
          </div>
          <div
            class="row q-mt-lg"
            v-if="authStore.hasObjectPermission(contact, ['patch_assigned']) || authStore.hasObjectPermission(contact, ['remove_assigned'])"
          >
            <div class="col-12 flex">
              <a class="omni-link cursor-pointer" @click="setContactToEdit(contact)" v-if="authStore.hasObjectPermission(contact, ['patch_assigned'])">
                >
                {{ $t('Customer.' + capitalize(itemsService) + '.Edit contact') }}</a
              >
            </div>
            <div class="col-12 flex">
              <a class="omni-link cursor-pointer" @click="saveDomainContact(contact, true)" v-if="authStore.hasObjectPermission(contact, ['remove_assigned'])">
                >
                {{ $t('Customer.' + capitalize(itemsService) + '.Delete contact') }}</a
              >
            </div>
          </div>
        </q-card-section>
      </q-card>
      <div class="flex justify-center">
        <q-btn
          v-if="items$.data.length > 0 && limitRef < items$.total"
          class="bg-white q-my-md shadow-1 font-size-rem-11"
          color="primary"
          icon="sym_o_keyboard_double_arrow_down"
          outline
          :label="$t('Shared.Globals.Show more')"
          :loading="items$.isPending"
          @click="limitRef += 10"
        />
      </div>
    </template>

    <template v-if="!items$.isPending && items$.data?.length === 0 && !contactToEdit">
      <q-card class="rounded-borders q-mb-md" flat bordered>
        <q-card-section class="q-pa-lg text-center font-size-rem-13">
          <div class="row">
            <div class="col flex column">{{ $t('Customer.' + capitalize(itemsService) + '.No contacts found') }}</div>
          </div>
          <div class="row q-mt-lg" v-if="authStore.hasServicePermission(itemsService, ['create_assigned']) && !!!items$.latestQuery?.query?.textSearch">
            <div class="col flex justify-center">
              <q-btn
                :class="$q.screen.xs ? 'q-mb-sm' : ''"
                icon="add"
                class="q-mb-md q-py-sm"
                color="primary"
                :label="$t(translationString + 'New item')"
                :aria-label="$t(translationString + 'New item')"
                @click.prevent="newContact"
              >
                <q-tooltip v-if="!$q.screen.gt.sm" :delay="500">
                  {{ $t(translationString + 'New item', 2) }}
                </q-tooltip>
              </q-btn>
            </div>
          </div>
        </q-card-section>
      </q-card>
    </template>
  </div>
  <contact-form
    v-if="contactToEdit"
    v-model="contactToEdit"
    v-model:validation-errors="validationErrors"
    ref="contactForm"
    :schema="schema"
    @cancel-form="cancelContactForm"
    @new-form-data="saveDomainContact"
    :loading="contactLoading"
  />
</template>

<style scoped lang="scss"></style>
